import React, { useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Navigate } from 'react-router-dom';
import { UsersManagement } from '../components/admin/UsersManagement';
import { WorkshopsManagement } from '../components/admin/WorkshopsManagement';
import { ShopManagement } from '../components/admin/ShopManagement';
import { TutorialsManagement } from '../components/admin/TutorialsManagement';
import { FilesManagement } from '../components/admin/FilesManagement';
import { BannerTextsManagement } from '../components/admin/BannerTextsManagement';
import { SendGiftManagement } from '../components/admin/SendGiftManagement';
import { OrdersManagement } from '../components/admin/OrdersManagement';
import { CheckoutSessionsManagement } from '../components/admin/CheckoutSessionsManagement';
import { PurchaseStats } from '../components/admin/PurchaseStats';
import { DealsManagement } from '../components/admin/DealsManagement';
import { AmazonSearchPayloadManagement } from '../components/admin/AmazonSearchPayloadManagement';
import { ManualDealsManagement } from '../components/admin/ManualDealsManagement';
import { ChatQuestionsManagement } from '../components/admin/ChatQuestionsManagement';
import { PromptToImage } from '../components/PromptToImage';
import { PromptToImageUnfiltered } from '../components/PromptToImageUnfiltered';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { user } = useAuth();
  const [value, setValue] = useState(0);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Redirect if not super admin
  if (user?.role !== 'superAdmin') {
    return <Navigate to="/" replace />;
  }

  const isRTL = currentLang === 'he';

  const tabs = [
    { label: t('admin.tabs.users'), index: 0 },
    { label: t('admin.tabs.workshops'), index: 1 },
    { label: t('admin.tabs.shop'), index: 2 },
    { label: t('admin.tabs.tutorials'), index: 3 },
    { label: t('admin.tabs.files'), index: 4 },
    { label: t('admin.tabs.bannerTexts'), index: 5 },
    { label: t('admin.tabs.sendGift'), index: 6 },
    { label: t('admin.tabs.orders'), index: 7 },
    { label: t('admin.tabs.checkoutSessions'), index: 8 },
    { label: t('admin.tabs.purchaseStats'), index: 9 },
    { label: 'Cricut Deals', index: 10 },
    { label: 'Amazon Search Payloads', index: 11 },
    { label: 'Manual Amazon Deals', index: 12 },
    { label: 'Chat Questions', index: 13 },
    { label: t('pages.freeTools.tabs.promptToImage'), index: 14 },
    { label: t('pages.freeTools.tabs.promptToImageUnfiltered'), index: 15 }
  ];

  // Reverse the tabs array for RTL
  const orderedTabs = isRTL ? [...tabs].reverse() : tabs;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        <Tabs 
          value={isRTL ? (tabs.length - 1 - value) : value}
          onChange={(e, newValue) => handleChange(e, isRTL ? (tabs.length - 1 - newValue) : newValue)}
          aria-label="admin tabs"
          variant="scrollable"
          scrollButtons={false}
          sx={{
            '& .MuiTabs-flexContainer': {
              justifyContent: isRTL ? 'flex-end' : 'flex-start',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }
          }}
        >
          {orderedTabs.map((tab) => (
            <Tab key={tab.index} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <UsersManagement />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <WorkshopsManagement />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <ShopManagement />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <TutorialsManagement />
      </TabPanel>
      <TabPanel value={value} index={4}>
        <FilesManagement />
      </TabPanel>
      <TabPanel value={value} index={5}>
        <BannerTextsManagement />
      </TabPanel>
      <TabPanel value={value} index={6}>
        <SendGiftManagement />
      </TabPanel>
      <TabPanel value={value} index={7}>
        <OrdersManagement />
      </TabPanel>
      <TabPanel value={value} index={8}>
        <CheckoutSessionsManagement />
      </TabPanel>
      <TabPanel value={value} index={9}>
        <PurchaseStats />
      </TabPanel>
      <TabPanel value={value} index={10}>
        <DealsManagement />
      </TabPanel>
      <TabPanel value={value} index={11}>
        <AmazonSearchPayloadManagement />
      </TabPanel>
      <TabPanel value={value} index={12}>
        <ManualDealsManagement />
      </TabPanel>
      <TabPanel value={value} index={13}>
        <ChatQuestionsManagement />
      </TabPanel>
      <TabPanel value={value} index={14}>
        <PromptToImage />
      </TabPanel>
      <TabPanel value={value} index={15}>
        <PromptToImageUnfiltered />
      </TabPanel>
    </Box>
  );
}; 